import type { HTMLAttributes, ReactNode, Ref } from 'react'

/**
 * Visual treatment of the surface.
 *
 * - `elevated`: opaque background, border, and shadow. Matches the chrome
 *   used by dialogs and panel containers.
 * - `outlined`: opaque background and border, no shadow. Use for inline
 *   cards that should not appear to float.
 * - `flat`: foundation-tier background, no border or shadow. Use as a
 *   plain regional container (e.g. an app-level layout slot).
 */
export const SurfaceVariant = {
  Elevated: 'elevated',
  Outlined: 'outlined',
  Flat: 'flat',
  /** Secondary-tier background, no border or shadow: an inset field panel
   *  inside a primary surface (e.g. a question's follow-up inputs). */
  Secondary: 'secondary',
} as const

export type SurfaceVariant =
  (typeof SurfaceVariant)[keyof typeof SurfaceVariant]

/** Corner radius (maps to design tokens --radius-xs/sm/md/lg). */
export const SurfaceRadius = {
  None: 'none',
  ExtraSmall: 'xs',
  Small: 'sm',
  Medium: 'md',
  Large: 'lg',
} as const

export type SurfaceRadius = (typeof SurfaceRadius)[keyof typeof SurfaceRadius]

/**
 * Shadow elevation tier. Only takes effect when `variant=elevated`;
 * ignored for `outlined` and `flat`.
 */
export const SurfaceElevation = {
  ExtraSmall: 'xs',
  Small: 'sm',
  Medium: 'md',
  Large: 'lg',
} as const

export type SurfaceElevation =
  (typeof SurfaceElevation)[keyof typeof SurfaceElevation]

/**
 * Generic visual container primitive. Wraps content with a tokenised
 * background, border, shadow, and radius. Other components (dialog,
 * panel chrome) can compose this primitive instead of re-declaring the
 * same Tailwind class recipe.
 *
 * Padding is intentionally *not* a prop. Surfaces in this design system
 * are chrome containers whose children typically have regional padding
 * (header/body/footer). Use `className="p-3"` when uniform padding is
 * actually wanted.
 */
export type SurfaceProps = HTMLAttributes<HTMLDivElement> & {
  ref?: Ref<HTMLDivElement>
  /** Visual treatment. Defaults to `elevated`. */
  variant?: SurfaceVariant
  /** Corner radius. Defaults to `md`. */
  radius?: SurfaceRadius
  /**
   * Shadow tier. Defaults to `md`. Ignored when variant is not
   * `elevated`.
   */
  elevation?: SurfaceElevation
  children?: ReactNode
}
